import React, { useCallback, useEffect, useRef, useState } from "react";
import { Maximize2, Minimize2, Radio } from "lucide-react";
import { mount as mountMeeting } from "../../utils/gratsVideoEmbed";
import { tokenColor } from "../../utils/themeColor";
import Shimmer from "../Common/Shimmer";
import { AppButton, Callout, IconButton, cn } from "../ui";

/**
 * GratsMeetEmbed — the live class, running on our own meeting SDK.
 *
 * The platform ships its complete interface (stage, participants, chat,
 * controls, screen share, recording, device switching) as an embeddable page.
 * There is deliberately no meeting UI in this file: rebuilding one here would
 * mean two implementations of the same screen drifting apart, and the media
 * engine would land in this bundle.
 *
 * ── No links, anywhere ────────────────────────────────────────────────────
 * The only credential this component receives is a GRANT that our own server
 * minted after checking enrolment. There is no join URL, no passcode and no
 * "open in a new tab" — `hideInvite` also switches off the platform's own
 * invite control, because the LMS is what decides who is in a class.
 *
 * ── Lifecycle ─────────────────────────────────────────────────────────────
 * `destroy()` in the cleanup is what releases the camera and microphone.
 * Unmounting without it leaves the camera light on after the trainer has left
 * the page — the single most common way to get this wrong.
 *
 * ── A dropped connection is not the end of the class ─────────────────────
 * The meeting reports every departure as `left`, with a reason. Most are the
 * person leaving or the class ending, and go to `onMeetingEnd`. A lost
 * connection is neither, and treating it as one cost whole classes: the
 * trainer's page answers `onMeetingEnd` by ENDING THE CLASS for everybody, so a
 * minute of bad Wi-Fi sent every student home and closed the recording. With
 * `onRejoin`, those departures rejoin in place instead — a fresh grant from the
 * parent, the frame mounted again — and the platform continues the same
 * recording. Joining from another tab or device is not an ending either.
 *
 * Props mirror the other embeds in this folder so `MeetingProviderRouter` can
 * treat all three the same:
 *   joinConfig    { provider, mode, grant, embedBaseUrl, role, userName, … }
 *   onMeetingEnd  called once, when the meeting is over for this person
 *   onRejoin      optional; resolves a fresh joinConfig (a new grant). Without
 *                 it a dropped connection ends the meeting, as it always did.
 *   endLabel      what the button that gives up and calls onMeetingEnd says
 *   leaveUrl      kept for signature parity; navigation is the parent's job
 *   onStateChange optional; told { status, recordingState } as they change, for
 *                 a host page deciding what to offer around the meeting
 */

/** Departures that are the network's doing, not the person's. */
const RECONNECT_REASONS = new Set(["connection_lost", "server_shutdown", "unknown"]);
/** The same person joined from another tab or device. */
const MOVED_REASON = "duplicate_identity";
/**
 * How long the old frame gets before it is torn down. Told to leave, it hands
 * what it recorded while the connection was down to disk — the platform's
 * recorder sends it on the next join; tearing the frame down at once loses the
 * seconds it had not written yet.
 */
const FLUSH_MS = 2500;
const retryDelayMs = (attempt) => Math.min(2000 * 2 ** attempt, 15000);

const GratsMeetEmbed = ({ joinConfig, onMeetingEnd, onRejoin, endLabel = "Leave class", onStateChange }) => {
  const containerRef = useRef(null);
  const surfaceRef = useRef(null);

  // loading | in-meeting | reconnecting | elsewhere | error
  const [status, setStatus] = useState("loading");
  const [errorMessage, setErrorMessage] = useState("");
  const [rejoinError, setRejoinError] = useState("");
  const [participantCount, setParticipantCount] = useState(0);
  const [recordingState, setRecordingState] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  // Which grant is mounted. A rejoin swaps in a fresh one under a new key.
  const [session, setSession] = useState({ config: joinConfig, key: 0 });

  // The SDK can report "ended" and "left" for the same departure, and the
  // parent navigates away on the first one — so it must only fire once.
  const endedRef = useRef(false);
  const onMeetingEndRef = useRef(onMeetingEnd);
  onMeetingEndRef.current = onMeetingEnd;
  const onRejoinRef = useRef(onRejoin);
  onRejoinRef.current = onRejoin;
  const onStateChangeRef = useRef(onStateChange);
  onStateChangeRef.current = onStateChange;

  useEffect(() => {
    if (onStateChangeRef.current) onStateChangeRef.current({ status, recordingState });
  }, [status, recordingState]);

  const meetingRef = useRef(null);
  const statusRef = useRef(status);
  statusRef.current = status;
  const unmountedRef = useRef(false);
  const rejoiningRef = useRef(false);
  // Ends the current wait between attempts early ("Rejoin now", or back online).
  const wakeRef = useRef(null);

  useEffect(() => () => {
    unmountedRef.current = true;
    if (wakeRef.current) wakeRef.current();
  }, []);

  const fireEnded = useCallback(() => {
    if (endedRef.current) return;
    endedRef.current = true;
    if (onMeetingEndRef.current) onMeetingEndRef.current();
  }, []);

  /** Waits `ms`, or until the browser is back online, or until woken. */
  const pause = useCallback((ms) => new Promise((resolve) => {
    let timer = null;
    const done = () => {
      clearTimeout(timer);
      window.removeEventListener("online", done);
      if (wakeRef.current === done) wakeRef.current = null;
      resolve();
    };
    timer = setTimeout(done, ms);
    window.addEventListener("online", done);
    wakeRef.current = done;
  }), []);

  /**
   * Gets a fresh grant and mounts the meeting again.
   *
   * Keeps trying while the failure is the network's — offline, a timeout, a
   * 5xx — and stops on an answer from our server (a 4xx: the class has ended,
   * or this person may no longer join it), which no retry would change.
   */
  const rejoin = useCallback(async () => {
    if (rejoiningRef.current) {
      if (wakeRef.current) wakeRef.current();
      return;
    }
    rejoiningRef.current = true;
    setRejoinError("");
    try {
      for (let attempt = 0; !unmountedRef.current; attempt += 1) {
        if (attempt > 0 || navigator.onLine === false) {
          // eslint-disable-next-line no-await-in-loop
          await pause(navigator.onLine === false ? 15000 : retryDelayMs(attempt));
          if (unmountedRef.current) return;
          if (navigator.onLine === false) continue;
        }
        let fresh;
        try {
          // eslint-disable-next-line no-await-in-loop
          fresh = await onRejoinRef.current();
        } catch (err) {
          const answered = err?.response?.status;
          if (answered && answered < 500) {
            setRejoinError(err.response.data?.message || "This class can no longer be joined.");
            return;
          }
          continue;
        }
        if (unmountedRef.current) return;
        try {
          if (meetingRef.current) meetingRef.current.leave();
        } catch (_) {
          // Already gone; nothing to flush.
        }
        // eslint-disable-next-line no-await-in-loop
        await new Promise((resolve) => setTimeout(resolve, FLUSH_MS));
        if (unmountedRef.current) return;
        setStatus("loading");
        setSession((current) => ({ config: fresh, key: current.key + 1 }));
        return;
      }
    } finally {
      rejoiningRef.current = false;
    }
  }, [pause]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    const joinConfig = session.config;
    if (!joinConfig?.grant || !joinConfig?.embedBaseUrl) {
      setErrorMessage("Class configuration is incomplete. Please refresh the page.");
      setStatus("error");
      return undefined;
    }

    let meeting;
    try {
      meeting = mountMeeting(container, {
        baseUrl: joinConfig.embedBaseUrl,
        grant: joinConfig.grant,
        // The LMS issues invitations; the platform must not offer its own.
        hideInvite: true,
        // Carry the institute's brand into the meeting. `--primary` is a bare
        // HSL triplet, so it is read through tokenColor rather than passed as
        // `var(--primary)`, which would mean nothing on the other origin.
        theme: { accent: tokenColor("--primary") },
      });
    } catch (mountErr) {
      setErrorMessage(mountErr.message || "Could not start the class.");
      setStatus("error");
      return undefined;
    }

    meetingRef.current = meeting;

    const offJoined = meeting.on("joined", () => {
      setRejoinError("");
      setStatus("in-meeting");
    });
    const offLeft = meeting.on("left", (payload) => {
      const reason = payload?.reason;
      // Already on the way back in; this frame is about to be replaced.
      if (statusRef.current === "reconnecting") return;
      if (onRejoinRef.current && RECONNECT_REASONS.has(reason)) {
        setStatus("reconnecting");
        rejoin();
        return;
      }
      if (onRejoinRef.current && reason === MOVED_REASON) {
        // Stop recording here at once: the other tab or device has taken the
        // meeting over and is recording it, and two recorders writing one
        // recording fight over its chunk numbers.
        try {
          meeting.leave();
        } catch (_) {
          // Already gone.
        }
        setStatus("elsewhere");
        return;
      }
      fireEnded();
    });
    const offCount = meeting.on("participantCountChanged", ({ count }) => setParticipantCount(count));
    const offRecording = meeting.on("recordingStatusChanged", ({ state }) => setRecordingState(state));
    const offError = meeting.on("error", ({ message, fatal }) => {
      if (!fatal || statusRef.current === "reconnecting") return;
      setErrorMessage(message || "The class could not be joined.");
      setStatus("error");
    });

    return () => {
      offJoined();
      offLeft();
      offCount();
      offRecording();
      offError();
      if (meetingRef.current === meeting) meetingRef.current = null;
      // Releases the camera and microphone. Not optional.
      meeting.destroy();
    };
    // Mounted once per grant: the parent gates rendering until the first one
    // is loaded, and only a rejoin swaps it — re-mounting for any other reason
    // would drop the person out of the meeting.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.key]);

  // ── Fullscreen ────────────────────────────────────────────────────────────
  // A trainer presenting wants the whole screen, and the browser only grants
  // that from a user gesture on an element we own.
  useEffect(() => {
    const sync = () => setIsFullscreen(document.fullscreenElement === surfaceRef.current);
    document.addEventListener("fullscreenchange", sync);
    return () => document.removeEventListener("fullscreenchange", sync);
  }, []);

  const toggleFullscreen = () => {
    const surface = surfaceRef.current;
    if (!surface) return;
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(() => {});
    } else {
      surface.requestFullscreen?.().catch(() => {});
    }
  };

  const isRecording = recordingState === "recording";

  return (
    <div className="tw-flex tw-flex-col tw-gap-3">
      {status === "error" ? (
        <Callout tone="danger" role="alert">
          {errorMessage || "Unable to join the class. Please refresh the page, or ask your trainer to restart it."}
        </Callout>
      ) : null}

      <div
        ref={surfaceRef}
        className={cn(
          // `tw-border-solid` is not decoration: Preflight is off, so a bare
          // `tw-border` is a width on a border whose style is still `none` and
          // it paints nothing.
          "tw-relative tw-overflow-hidden tw-rounded-lg tw-border tw-border-solid tw-border-line tw-bg-surface-raised",
          // Vertical space is the constraint on a 15.6" laptop at 150% scale
          // (~1280×720 effective), so the stage takes the viewport it can and
          // no more. The iframe fills its container, and a container with no
          // height renders nothing at all.
          isFullscreen
            ? "tw-h-screen tw-rounded-none tw-border-0"
            : "tw-h-[calc(100vh-13rem)] tw-min-h-[420px]"
        )}
      >
        <div ref={containerRef} className="tw-h-full tw-w-full" />

        {status === "loading" ? (
          <div className="tw-absolute tw-inset-0 tw-flex tw-flex-col tw-justify-center tw-gap-4 tw-bg-surface-raised tw-p-6">
            {/* Mirrors the layout that is about to appear: stage, then a strip
                of controls — not a spinner that says nothing about the wait. */}
            <Shimmer height={280} />
            <Shimmer height={44} />
            <p className="tw-text-center tw-text-sm tw-text-content-muted">
              Connecting to your class…
            </p>
          </div>
        ) : null}

        {/* Covers the old frame while it waits to be replaced; underneath it
            the meeting's own "you left" screen would read as the end. */}
        {status === "reconnecting" || status === "elsewhere" ? (
          <div className="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-bg-surface-raised tw-p-6">
            <div className="tw-flex tw-w-full tw-max-w-md tw-flex-col tw-gap-3" role="status" aria-live="polite">
              {status === "elsewhere" ? (
                <Callout tone="info">
                  This class is open in another tab or on another device.
                </Callout>
              ) : rejoinError ? (
                <Callout tone="danger">{rejoinError}</Callout>
              ) : (
                <Callout tone="warning">
                  Your connection dropped. The class is still running, and you will be put
                  back in as soon as the connection returns.
                </Callout>
              )}
              <div className="tw-flex tw-flex-wrap tw-justify-end tw-gap-2">
                {status === "reconnecting" ? (
                  <AppButton variant="outline" size="sm" onClick={fireEnded}>
                    {endLabel}
                  </AppButton>
                ) : null}
                <AppButton
                  size="sm"
                  onClick={() => {
                    setStatus("reconnecting");
                    rejoin();
                  }}
                >
                  {status === "elsewhere" ? "Use this tab" : rejoinError ? "Try again" : "Rejoin now"}
                </AppButton>
              </div>
            </div>
          </div>
        ) : null}
      </div>

      {/* Status strip. Deliberately outside the meeting surface: the platform
          owns everything inside the frame, and a second set of controls
          overlapping its own would be two answers to the same question. */}
      <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2">
        <div className="tw-flex tw-items-center tw-gap-3 tw-text-sm tw-text-content-muted">
          {status === "in-meeting" ? (
            <span>
              {participantCount === 1 ? "1 person" : `${participantCount} people`} in the class
            </span>
          ) : null}
          {isRecording ? (
            <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-font-medium tw-text-danger">
              <Radio size={14} aria-hidden="true" />
              Recording
            </span>
          ) : null}
        </div>

        <div className="tw-flex tw-items-center tw-gap-2">
          <IconButton
            label={isFullscreen ? "Exit full screen" : "Full screen"}
            icon={isFullscreen ? Minimize2 : Maximize2}
            variant="outline"
            size="sm"
            onClick={toggleFullscreen}
          />
          {status === "error" ? (
            <AppButton variant="outline" size="sm" onClick={() => window.location.reload()}>
              Try again
            </AppButton>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default GratsMeetEmbed;
